/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Shield, AlertCircle, Zap, Bell, Search } from 'lucide-react';
import { unauthenticatedFetch } from './utils/api';
import AnimatedLogo from './components/AnimatedLogo';

// Hook pour animer un compteur
const useCountUp = (end, duration = 1500) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime = null;
    let animationId = null;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function (easeOutExpo)
      const easeOut = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(end * easeOut));
      
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };
    
    // Délai initial pour laisser le temps au composant de s'afficher
    const timeoutId = setTimeout(() => {
      animationId = requestAnimationFrame(animate);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [end, duration]);

  return count;
};

// Valide qu'une URL de redirection est sûre (pas d'open redirect ou XSS)
const isValidRedirectPath = (path) => {
  if (!path || typeof path !== 'string') return false;
  
  // Doit commencer par / (chemin relatif au domaine)
  if (!path.startsWith('/')) return false;
  
  // Bloquer les protocol-relative URLs (//evil.com)
  if (path.startsWith('//')) return false;
  
  // Bloquer les schemes dangereux (javascript:, data:, vbscript:, etc.)
  const dangerousSchemes = /^[a-zA-Z][a-zA-Z0-9+.-]*:/i;
  if (dangerousSchemes.test(path)) return false;
  
  // Bloquer les tentatives d'encodage (%2f%2f = //)
  try {
    const decoded = decodeURIComponent(path);
    if (decoded.startsWith('//') || dangerousSchemes.test(decoded)) return false;
  } catch (e) {
    return false;
  }
  
  return true;
};

// ============================================
// ANIMATION: Deep Scan Sonar
// ============================================
// Generate random characters for token samples
const randomChars = (len, charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') => {
  let result = '';
  for (let i = 0; i < len; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
};

const SECRET_TYPES = [
  { 
    label: 'OPENAI_KEY', 
    color: '#ef4444', 
    generateSample: () => `sk-proj-${randomChars(4)}...${randomChars(4)}`
  },
  { 
    label: 'SLACK_TOKEN', 
    color: '#f59e0b', 
    generateSample: () => `xoxb-${randomChars(4)}...${randomChars(4)}`
  },
  { 
    label: 'JWT_SECRET', 
    color: '#eab308', 
    generateSample: () => `eyJhbGc...${randomChars(6)}`
  },
  { 
    label: 'AWS_KEY', 
    color: '#f97316', 
    generateSample: () => `AKIA${randomChars(4, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789')}...${randomChars(4, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789')}`
  },
  { 
    label: 'GITHUB_TOKEN', 
    color: '#a855f7', 
    generateSample: () => `ghp_${randomChars(6)}...${randomChars(4)}`
  },
  { 
    label: 'STRIPE_KEY', 
    color: '#ec4899', 
    generateSample: () => `sk_live_${randomChars(4)}...${randomChars(4)}`
  },
  { 
    label: 'ANTHROPIC_KEY', 
    color: '#d97706', 
    generateSample: () => `sk-ant-api03-${randomChars(4)}...${randomChars(4)}`
  },
  { 
    label: 'VERCEL_TOKEN', 
    color: '#64748b', 
    generateSample: () => `vercel_${randomChars(6)}...${randomChars(4)}`
  },
  { 
    label: 'SUPABASE_KEY', 
    color: '#10b981', 
    generateSample: () => `sbp_${randomChars(8)}...${randomChars(4)}`
  },
  { 
    label: 'RESEND_KEY', 
    color: '#3b82f6', 
    generateSample: () => `re_${randomChars(6)}...${randomChars(4)}`
  },
];

// Données des rayons pré-calculées (évite recréation à chaque render)
const LIGHT_RAYS = [
  { x: 8, width: 25, delay: 0, duration: 8, opacity: 0.025 },
  { x: 22, width: 40, delay: 2, duration: 10, opacity: 0.03 },
  { x: 45, width: 35, delay: 1, duration: 9, opacity: 0.025 },
  { x: 65, width: 50, delay: 3, duration: 11, opacity: 0.035 },
  { x: 85, width: 30, delay: 0.5, duration: 8.5, opacity: 0.025 },
  { x: 15, width: 20, delay: 4, duration: 12, opacity: 0.02 },
  { x: 55, width: 45, delay: 2.5, duration: 9.5, opacity: 0.03 },
  { x: 75, width: 25, delay: 1.5, duration: 10, opacity: 0.025 },
];
const THIN_RAYS = [12, 35, 58, 78, 92];

// Rayons de lumière descendants (caustics réalistes)
const LightRays = React.memo(() => {
  const [isReady, setIsReady] = useState(false);
  
  // Attendre que le composant soit monté avant de lancer les animations
  // Cela évite le saut saccadé au premier render
  useEffect(() => {
    // Utiliser requestAnimationFrame pour s'assurer que le layout est calculé
    const raf = requestAnimationFrame(() => {
      setIsReady(true);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div 
      className="absolute inset-0 overflow-hidden pointer-events-none"
      style={{ contain: 'strict' }}
    >
      {LIGHT_RAYS.map((ray, i) => (
        <div
          key={i}
          className="absolute top-0 h-full"
          style={{
            left: `${ray.x}%`,
            width: ray.width,
            background: `linear-gradient(
              180deg,
              rgba(56, 189, 248, ${ray.opacity * 2}) 0%,
              rgba(34, 211, 238, ${ray.opacity * 1.5}) 20%,
              rgba(34, 211, 238, ${ray.opacity}) 50%,
              rgba(34, 211, 238, ${ray.opacity * 0.5}) 80%,
              transparent 100%
            )`,
            transform: 'skewX(-5deg) translateX(0) translateZ(0)',
            animation: isReady ? `lightray-sway ${ray.duration}s ease-in-out infinite` : 'none',
            animationDelay: isReady ? `${ray.delay}s` : '0s',
            filter: 'blur(8px)',
            backfaceVisibility: 'hidden',
          }}
        />
      ))}
      {/* Rayons plus fins et plus lumineux */}
      {THIN_RAYS.map((x, i) => (
        <div
          key={`thin-${i}`}
          className="absolute top-0 h-full"
          style={{
            left: `${x}%`,
            width: 3 + i * 2,
            background: `linear-gradient(
              180deg,
              rgba(255, 255, 255, 0.05) 0%,
              rgba(165, 243, 252, 0.03) 30%,
              rgba(34, 211, 238, 0.02) 60%,
              transparent 100%
            )`,
            transform: 'skewX(-3deg) translateX(0) translateZ(0)',
            animation: isReady ? `lightray-sway ${7 + i}s ease-in-out infinite` : 'none',
            animationDelay: isReady ? `${i * 0.8}s` : '0s',
            filter: 'blur(3px)',
            backfaceVisibility: 'hidden',
          }}
        />
      ))}
      <style>{`
        @keyframes lightray-sway {
          0%, 100% { 
            transform: skewX(-5deg) translateX(0) translateZ(0); 
            opacity: 1;
          }
          25% { 
            transform: skewX(-3deg) translateX(10px) translateZ(0); 
            opacity: 0.8;
          }
          50% { 
            transform: skewX(-7deg) translateX(-5px) translateZ(0); 
            opacity: 1;
          }
          75% { 
            transform: skewX(-4deg) translateX(8px) translateZ(0); 
            opacity: 0.9;
          }
        }
      `}</style>
    </div>
  );
});

// Composant de détection avec effet de reveal progressif (optimisé pour CPU)
const DetectionReveal = React.memo(({ detection, sonarWaves }) => {
  const [revealedChars, setRevealedChars] = useState(0);
  const [glitchText, setGlitchText] = useState('');
  const [isDistorted, setIsDistorted] = useState(false);
  const [appearOpacity, setAppearOpacity] = useState(0); // Fade-in progressif
  const fullText = `${detection.label}: ${detection.sample}`;
  const revealDuration = 1200; // ms pour révéler tout le texte
  const distortionTimeoutRef = useRef(null);
  
  // Fade-in progressif à l'apparition (évite l'effet "flou soudain")
  useEffect(() => {
    // Petit délai puis fade-in rapide
    const timeout = setTimeout(() => {
      setAppearOpacity(1);
    }, 50);
    return () => clearTimeout(timeout);
  }, []);
  
  // Effet de reveal progressif - s'arrête une fois terminé
  useEffect(() => {
    const startTime = detection.createdAt;
    let animationId;
    let lastUpdate = 0;
    
    const animate = (timestamp) => {
      // Limiter à ~15fps pour le reveal (suffisant pour l'effet)
      if (timestamp - lastUpdate < 66) {
        animationId = requestAnimationFrame(animate);
        return;
      }
      lastUpdate = timestamp;
      
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / revealDuration, 1);
      const charsToReveal = Math.floor(progress * fullText.length);
      setRevealedChars(charsToReveal);
      
      // Générer du texte glitch pour les caractères non révélés
      if (charsToReveal < fullText.length) {
        const glitchChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let glitch = '';
        for (let i = charsToReveal; i < fullText.length; i++) {
          if (fullText[i] === ' ' || fullText[i] === ':' || fullText[i] === '-' || fullText[i] === '_') {
            glitch += fullText[i];
          } else {
            glitch += glitchChars[Math.floor(Math.random() * glitchChars.length)];
          }
        }
        setGlitchText(glitch);
        animationId = requestAnimationFrame(animate);
      } else {
        setGlitchText('');
        // Animation terminée, ne plus boucler
      }
    };
    
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [detection.createdAt, fullText, revealDuration]);

  // Détecter si une onde sonar touche cette détection (optimisé)
  // Ne pas déclencher la distorsion pendant les 500 premières ms (période d'apparition)
  useEffect(() => {
    const age = Date.now() - detection.createdAt;
    // Ignorer les distorsions pendant la phase d'apparition
    if (age < 500) return;
    
    const sonarOriginX = 15;
    const sonarOriginY = 15;
    const dx = detection.x - sonarOriginX;
    const dy = detection.y - sonarOriginY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Vérifier si une onde est proche
    for (const wave of sonarWaves) {
      const waveRadius = wave.scale * 60;
      if (Math.abs(distance - waveRadius) < 8) {
        if (!isDistorted) {
          setIsDistorted(true);
          if (distortionTimeoutRef.current) clearTimeout(distortionTimeoutRef.current);
          distortionTimeoutRef.current = setTimeout(() => setIsDistorted(false), 200);
        }
        break;
      }
    }
    
    return () => {
      if (distortionTimeoutRef.current) clearTimeout(distortionTimeoutRef.current);
    };
  }, [sonarWaves, detection.x, detection.y, detection.createdAt, isDistorted]);

  // Calculer l'opacité basée sur l'âge avec fade-out progressif
  const age = detection.createdAt ? Date.now() - detection.createdAt : 0;
  const lifetime = 5500; // Durée de vie totale avant suppression (6000ms)
  const fadeStart = 2500; // Commence à disparaître après 2.5s
  
  // Opacité: 1 pendant fadeStart ms, puis décroît linéairement jusqu'à 0
  let ageOpacity = 1;
  if (age > fadeStart) {
    ageOpacity = Math.max(0, 1 - (age - fadeStart) / (lifetime - fadeStart));
  }
  
  // Combiner l'opacité d'apparition avec l'opacité basée sur l'âge
  const opacity = ageOpacity * appearOpacity;
  const isRevealing = revealedChars < fullText.length;
  
  return (
    <div
      className="absolute"
      style={{ 
        left: `${detection.x}%`, 
        top: `${detection.y}%`, 
        transform: 'translate(-50%, -50%)',
        opacity: opacity,
        transition: 'opacity 0.1s linear',
      }}
    >
      {/* Ping effect - taille fixe, pas de grossissement */}
      <div 
        className="absolute inset-0 rounded-full animate-ping"
        style={{ 
          backgroundColor: detection.color, 
          opacity: 0.5, 
          width: 20, 
          height: 20, 
          marginLeft: -10, 
          marginTop: -10,
        }}
      />
      {/* Core - effet glow subtil au passage du sonar, pas de scale */}
      <div 
        className="w-3 h-3 rounded-full"
        style={{ 
          backgroundColor: detection.color, 
          boxShadow: `0 0 ${isDistorted ? 20 : 12}px ${detection.color}`,
          transition: 'box-shadow 0.2s ease-out',
        }}
      />
      {/* Label avec reveal progressif - même opacité que le conteneur parent */}
      <div 
        className="absolute left-5 top-0 whitespace-nowrap text-xs font-mono px-2 py-0.5 rounded overflow-hidden"
        style={{ 
          backgroundColor: `${detection.color}20`, 
          color: detection.color, 
          boxShadow: isDistorted ? `0 0 8px ${detection.color}30` : 'none',
          transition: 'box-shadow 0.2s ease-out',
        }}
      >
        <span style={{ opacity: 0.9 }}>
          {fullText.slice(0, revealedChars)}
        </span>
        {isRevealing && (
          <span style={{ opacity: 0.4, color: detection.color }}>
            {glitchText}
          </span>
        )}
        {/* Curseur clignotant pendant le reveal */}
        {isRevealing && (
          <span 
            className="inline-block w-[2px] h-[10px] ml-[1px] align-middle"
            style={{ 
              backgroundColor: detection.color,
              animation: 'blink 0.1s infinite',
            }}
          />
        )}
      </div>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
});

// Particules générées une seule fois avec animation CSS
const generateParticles = () => Array.from({ length: 30 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  startY: Math.random() * 100,
  size: 1 + Math.random() * 3,
  duration: 15 + Math.random() * 20, // 15-35 secondes pour monter
  delay: Math.random() * -30, // Décalage négatif pour démarrer à différentes positions
  opacity: 0.1 + Math.random() * 0.3,
  sway: 3 + Math.random() * 5, // Amplitude du mouvement latéral
}));

const PARTICLES = generateParticles();

const DeepScanSonar = React.memo(() => {
  const [sonarWaves, setSonarWaves] = useState([]);
  const [detections, setDetections] = useState([]);
  const lastSecretIndexRef = React.useRef(-1);

  // Emit sonar waves (toutes les 4 secondes pour un effet plus calme)
  useEffect(() => {
    const interval = setInterval(() => {
      setSonarWaves(prev => [...prev.slice(-3), { id: Date.now(), scale: 0 }]);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Animate sonar waves (optimisé: 50ms au lieu de 30ms)
  useEffect(() => {
    const interval = setInterval(() => {
      setSonarWaves(prev => prev
        .map(w => ({ ...w, scale: w.scale + 0.013 })) // ajusté pour la même vitesse visuelle
        .filter(w => w.scale < 1.5)
      );
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Random detections - positioned in safe zones
  // Cards are roughly at Y: 60-75%, centered horizontally (X: 15-85%)
  // Text is at Y: 35-55%, centered
  useEffect(() => {
    const interval = setInterval(() => {
      const zone = Math.floor(Math.random() * 5);
      let x, y;
      
      switch(zone) {
        case 0: // top-left corner
          x = 5 + Math.random() * 20;
          y = 5 + Math.random() * 18;
          break;
        case 1: // top-right corner (not too far right)
          x = 40 + Math.random() * 20;
          y = 5 + Math.random() * 18;
          break;
        case 2: // left side (above cards level)
          x = 5 + Math.random() * 15;
          y = 28 + Math.random() * 18;
          break;
        case 3: // BELOW cards - bottom left
          x = 5 + Math.random() * 25;
          y = 82 + Math.random() * 12;
          break;
        case 4: // BELOW cards - bottom right (not too far right)
          x = 35 + Math.random() * 25;
          y = 82 + Math.random() * 12;
          break;
        default:
          x = 10;
          y = 10;
      }
      
      // Pick a random secret type, avoiding the same one twice in a row
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * SECRET_TYPES.length);
      } while (newIndex === lastSecretIndexRef.current && SECRET_TYPES.length > 1);
      lastSecretIndexRef.current = newIndex;
      
      const secret = SECRET_TYPES[newIndex];
      
      setDetections(prev => [...prev.slice(-6), {
        id: Date.now(),
        x, y,
        label: secret.label,
        color: secret.color,
        sample: secret.generateSample(),
        createdAt: Date.now(),
      }]);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Cleanup old detections
  useEffect(() => {
    const interval = setInterval(() => {
      setDetections(prev => prev.filter(d => Date.now() - d.createdAt < 6000));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      className="absolute inset-0 overflow-hidden"
      style={{ contain: 'layout style paint' }}
    >
      {/* Deep ocean gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] via-[#0d1f3c] to-[#051224]" />
      
      {/* Rayons de lumière descendants depuis la surface */}
      <LightRays />
      
      {/* Depth lines */}
      {[20, 40, 60, 80].map(y => (
        <div 
          key={y}
          className="absolute left-0 right-0 border-t border-cyan-500/5"
          style={{ top: `${y}%` }}
        >
          <span className="absolute right-4 -top-3 text-[10px] text-cyan-500/20 font-mono">
            -{y * 10}m
          </span>
        </div>
      ))}

      {/* Floating particles (sédiments) - animation CSS fluide */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{ contain: 'strict' }}
      >
        {PARTICLES.map(p => (
          <div
            key={p.id}
            className="absolute rounded-full bg-cyan-300"
            style={{
              left: `${p.x}%`,
              width: p.size,
              height: p.size,
              opacity: p.opacity,
              animation: `particle-rise ${p.duration}s linear infinite, particle-sway ${p.duration / 2}s ease-in-out infinite`,
              animationDelay: `${p.delay}s, ${p.delay}s`,
              backfaceVisibility: 'hidden',
              '--sway-amount': `${p.sway}px`,
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes particle-rise {
          0% { transform: translateY(0) translateZ(0); top: 105%; }
          100% { transform: translateY(0) translateZ(0); top: -5%; }
        }
        @keyframes particle-sway {
          0%, 100% { transform: translateX(0) translateZ(0); }
          50% { transform: translateX(var(--sway-amount, 5px)) translateZ(0); }
        }
      `}</style>

      {/* Sonar center point - positioned at top-left to avoid text */}
      <div className="absolute top-[15%] left-[15%]">
        <div className="w-3 h-3 rounded-full bg-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.6)]" />
      </div>

      {/* Sonar waves - emanate from top-left corner */}
      {sonarWaves.map(wave => (
        <div
          key={wave.id}
          className="absolute top-[15%] left-[15%] rounded-full border border-cyan-400/30 pointer-events-none"
          style={{
            width: `${wave.scale * 120}%`,
            height: `${wave.scale * 120}%`,
            opacity: 1 - wave.scale / 1.5,
            boxShadow: `0 0 10px rgba(34,211,238,${0.2 - wave.scale * 0.13})`,
            transform: 'translate(-50%, -50%) translateZ(0)',
            backfaceVisibility: 'hidden',
          }}
        />
      ))}

      {/* Detections avec reveal progressif et distorsion sonar */}
      {detections.map(d => (
        <DetectionReveal key={d.id} detection={d} sonarWaves={sonarWaves} />
      ))}

    </div>
  );
});

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer l'URL d'origine depuis le state de navigation avec validation
  const rawFrom = location.state?.from?.pathname || '/';
  const rawSearch = location.state?.from?.search || '';
  const from = isValidRedirectPath(rawFrom) ? rawFrom : '/';
  const searchParams = rawSearch.startsWith('?') || rawSearch === '' ? rawSearch : '';
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    twoFactorCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [tempToken, setTempToken] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await unauthenticatedFetch('/login', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      if (response.requires2FA) {
        setShowTwoFactor(true);
        setTempToken(response.tempToken);
      } else if (response.token) {
        onLogin({ token: response.token });
        // Rediriger vers l'URL d'origine ou la page d'accueil
        navigate(from + searchParams, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await unauthenticatedFetch('/verify-2fa', {
        method: 'POST',
        body: JSON.stringify({
          tempToken: tempToken,
          code: formData.twoFactorCode
        })
      });

      if (response.token) {
        onLogin({ token: response.token });
        // Rediriger vers l'URL d'origine ou la page d'accueil
        navigate(from + searchParams, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex">
      {/* Left: Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <AnimatedLogo src="/logo192.png" alt="GitGraber" size={40} interval={5000} />
            <div>
              <h1 className="font-semibold text-xl text-ink">GitGraber</h1>
              <p className="text-tiny text-ink-tertiary">Secret Detection Platform</p>
            </div>
          </div>

          {/* Form */}
          {!showTwoFactor ? (
            <form onSubmit={handleSubmit}>
              <h2 className="text-headline text-ink mb-2">Welcome back</h2>
              <p className="text-body text-ink-secondary mb-8">Sign in to your account to continue</p>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-danger-soft border border-danger/20 rounded-md mb-6">
                  <AlertCircle size={16} className="text-danger shrink-0" />
                  <p className="text-caption text-danger">{error}</p>
                </div>
              )}

              <div className="space-y-5">
                <div>
                  <label className="block text-caption font-medium text-ink mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@company.com"
                    required
                    className="w-full px-4 py-3 bg-surface-secondary border border-border rounded-md text-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-base"
                  />
                </div>

                <div>
                  <label className="block text-caption font-medium text-ink mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      required
                      className="w-full px-4 py-3 pr-12 bg-surface-secondary border border-border rounded-md text-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-base"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-tertiary hover:text-ink transition-base"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full mt-8 flex items-center justify-center gap-2 px-6 py-3 bg-accent text-white font-medium rounded-md hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-base"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleTwoFactorSubmit}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent-soft rounded-lg flex items-center justify-center">
                  <Shield className="text-accent" size={20} />
                </div>
                <div>
                  <h2 className="text-title text-ink">Two-Factor Authentication</h2>
                  <p className="text-caption text-ink-secondary">Enter your verification code</p>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-danger-soft border border-danger/20 rounded-md mb-6">
                  <AlertCircle size={16} className="text-danger shrink-0" />
                  <p className="text-caption text-danger">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-caption font-medium text-ink mb-2">Verification Code</label>
                <input
                  type="text"
                  name="twoFactorCode"
                  value={formData.twoFactorCode}
                  onChange={handleInputChange}
                  placeholder="000000"
                  maxLength={6}
                  required
                  autoFocus
                  className="w-full px-4 py-3 bg-surface-secondary border border-border rounded-md text-body text-ink text-center tracking-[0.5em] font-mono text-lg placeholder:text-ink-muted placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-base"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || formData.twoFactorCode.length !== 6}
                className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 bg-accent text-white font-medium rounded-md hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-base"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Verify</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowTwoFactor(false);
                  setFormData(prev => ({ ...prev, twoFactorCode: '' }));
                  setError('');
                }}
                className="w-full mt-3 text-caption text-ink-secondary hover:text-ink transition-base"
              >
                Back to login
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Right: Visual - Deep Scan Animation */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <DeepScanSonar />
        
        {/* Overlay content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none">
          <h2 className="text-3xl font-bold text-white mb-4 text-center drop-shadow-lg">
            Secrets can't hide
          </h2>
          <p className="text-lg text-cyan-100/70 leading-relaxed text-center max-w-sm">
            We scan the depths of public commits to find exposed credentials before attackers do.
          </p>
          
          <div className="mt-12 grid grid-cols-3 gap-4 w-full max-w-md pointer-events-auto">
            <FeatureCard icon={Zap} value={5} suffix="min" prefix="<" label="Detection time" />
            <FeatureCard icon={Bell} value={300} suffix="+" label="Alert patterns" />
            <FeatureCard icon={Search} value={24} suffix="/7" label="Monitoring" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant FeatureCard avec compteur animé et icône
const FeatureCard = React.memo(({ icon: Icon, value, suffix = '', prefix = '', label }) => {
  const animatedValue = useCountUp(value, 1500);
  
  return (
    <div className="p-4 rounded-lg bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 transition-all duration-300">
      <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-cyan-500/20 flex items-center justify-center">
        <Icon size={20} className="text-cyan-400" />
      </div>
      <p className="text-2xl font-bold text-white tabular-nums">
        {prefix}{animatedValue}{suffix}
      </p>
      <p className="text-sm text-white/50 mt-1">{label}</p>
    </div>
  );
});

export default Login;
